import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace('/api', '');

const TASHKENT_CENTER = { lat: 41.2995, lng: 69.2401 };

export default function LabInfoSettings() {
  const { t } = useLang();
  const [labs, setLabs] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const logoInputRef = useRef();
  const mapRef = useRef();
  const [mapReady, setMapReady] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    api.get('/owner/labs').then(r => {
      setLabs(r.data || []);
      if (r.data?.length) setSelectedLabId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedLabId) return;
    setLoading(true);
    api.get(`/owner/labs/${selectedLabId}/info`).then(r => {
      setInfo(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedLabId]);

  useEffect(() => {
    if (window.L) { setMapReady(true); return; }
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setMapReady(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!mapReady || !info || !mapRef.current) return;
    const L = window.L;
    if (mapRef.current._leaflet_id) return;

    const startLat = Number(info.lat) || TASHKENT_CENTER.lat;
    const startLng = Number(info.lng) || TASHKENT_CENTER.lng;
    const map = L.map(mapRef.current).setView([startLat, startLng], 13);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
    }).addTo(map);

    const marker = L.marker([startLat, startLng], { draggable: true }).addTo(map);
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      setInfo(prev => ({ ...prev, lat: pos.lat, lng: pos.lng }));
    });
    map.on('click', (e) => {
      marker.setLatLng(e.latlng);
      setInfo(prev => ({ ...prev, lat: e.latlng.lat, lng: e.latlng.lng }));
    });
  }, [mapReady, info]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/owner/labs/${selectedLabId}/info`, {
        name: info.name,
        address: info.address,
        description: info.description,
        phone: info.phone,
        working_hours: info.working_hours,
        lat: info.lat,
        lng: info.lng,
        instagram: info.instagram,
        telegram: info.telegram,
        parent_help_phone: info.parent_help_phone,
        emergency_phone: info.emergency_phone,
      });
      alert(t('labSaved'));
    } catch (e) {
      alert('❌ ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('photos', f);
      const res = await api.post(`/owner/labs/${selectedLabId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setInfo(prev => ({ ...prev, photos: res.data.photos }));
    } catch (e) {
      alert('❌ ' + (e.response?.data?.error || e.message));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const deletePhoto = async (url) => {
    if (!window.confirm(t('labConfirmDeletePhoto'))) return;
    try {
      const res = await api.delete(`/owner/labs/${selectedLabId}/photos`, { data: { url } });
      setInfo(prev => ({ ...prev, photos: res.data.photos }));
    } catch (e) { alert(e.message); }
  };

  const setCover = async (url) => {
    try {
      await api.post(`/owner/labs/${selectedLabId}/cover`, { cover_url: url });
      setInfo(prev => ({ ...prev, cover_url: url }));
      alert(t('labCoverChanged'));
    } catch (e) { alert(e.message); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post(`/owner/labs/${selectedLabId}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setInfo(prev => ({ ...prev, logo_url: res.data.logo_url }));
    } catch (e) {
      alert('❌ ' + (e.response?.data?.error || e.message));
    }
    setUploadingLogo(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const deleteLogo = async () => {
    if (!window.confirm('Logoni o\'chirasizmi?')) return;
    try {
      await api.delete(`/owner/labs/${selectedLabId}/logo`);
      setInfo(prev => ({ ...prev, logo_url: null }));
    } catch (e) { alert(e.message); }
  };

  const [payCreds, setPayCreds] = useState({
    click_merchant_id: '', click_service_id: '', click_secret: '',
    payme_merchant_id: '', payme_secret: '',
  });
  const [savingPay, setSavingPay] = useState(false);

  useEffect(() => {
    if (!info) return;
    setPayCreds({
      click_merchant_id: info.click_merchant_id || '',
      click_service_id:  info.click_service_id  || '',
      click_secret: '',
      payme_merchant_id: info.payme_merchant_id || '',
      payme_secret: '',
    });
  }, [info]);

  const savePaymentCreds = async () => {
    setSavingPay(true);
    try {
      const payload = {
        click_merchant_id: payCreds.click_merchant_id,
        click_service_id:  payCreds.click_service_id,
        payme_merchant_id: payCreds.payme_merchant_id,
      };
      if (payCreds.click_secret) payload.click_secret = payCreds.click_secret;
      if (payCreds.payme_secret) payload.payme_secret = payCreds.payme_secret;
      await api.patch(`/owner/labs/${selectedLabId}/payment-credentials`, payload);
      const r = await api.get(`/owner/labs/${selectedLabId}/info`);
      setInfo(r.data);
      setPayCreds(pc => ({ ...pc, click_secret: '', payme_secret: '' }));
      alert(t('labPaymentSaved'));
    } catch (e) {
      alert('❌ ' + (e.response?.data?.error || e.message));
    }
    setSavingPay(false);
  };

  const removeProvider = async (provider) => {
    if (!window.confirm(`${provider.toUpperCase()} ${t('labConfirmRemoveProvider')}`)) return;
    try {
      const payload = provider === 'click'
        ? { click_merchant_id: '', click_service_id: '', click_secret: '' }
        : { payme_merchant_id: '', payme_secret: '' };
      await api.patch(`/owner/labs/${selectedLabId}/payment-credentials`, payload);
      const r = await api.get(`/owner/labs/${selectedLabId}/info`);
      setInfo(r.data);
      alert(`✅ ${provider.toUpperCase()} ${t('labProviderRemoved')}`);
    } catch (e) { alert('❌ ' + (e.response?.data?.error || e.message)); }
  };

  const fullPhotoUrl = (url) => url?.startsWith('http') ? url : `${API_BASE}${url}`;

  const [hubSecret, setHubSecret] = useState(null); // yangi generate qilingan secret
  const [generatingHub, setGeneratingHub] = useState(false);
  const [hubCopied, setHubCopied] = useState(false);

  const generateHubSecret = async () => {
    if (!window.confirm('Yangi hub secret yaratiladi. Avvalgi secret ishlashdan to\'xtaydi — club-hub config.json yangilanishi kerak. Davom etasizmi?')) return;
    setGeneratingHub(true);
    try {
      const r = await api.post(`/labs/${selectedLabId}/hub-secret`);
      setHubSecret(r.data.hub_secret);
    } catch (e) {
      alert('❌ ' + (e.response?.data?.error || e.message));
    }
    setGeneratingHub(false);
  };

  const copyHubSecret = () => {
    navigator.clipboard.writeText(hubSecret);
    setHubCopied(true);
    setTimeout(() => setHubCopied(false), 2000);
  };

  const downloadHubConfig = async () => {
    try {
      const r = await api.get(`/labs/${selectedLabId}/hub-config`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `hub-config-lab${selectedLabId}.json`;
      a.click();
    } catch (e) {
      alert('❌ ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ marginBottom: 16, fontSize: 22 }}>{t('labInfoTitle')}</h1>

      <div style={{ marginBottom: 16 }}>
        <label style={{ marginRight: 10 }}>{t('labInfoSelectClub')}</label>
        <select value={selectedLabId || ''} onChange={e => setSelectedLabId(Number(e.target.value))}
                style={{ padding: 8, minWidth: 250, background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)' }}>
          {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {loading || !info ? <div>{t('loading')}</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* CHAP: MA'LUMOTLAR */}
          <div>
            <h3 style={{ marginBottom: 12, color: 'var(--accent)' }}>{t('labInfoDataSection')}</h3>

            <F label={t('labFieldName')}>
              <input value={info.name || ''} onChange={e => setInfo({ ...info, name: e.target.value })}
                     style={inputStyle} />
            </F>
            <F label={t('labFieldAddress')}>
              <input value={info.address || ''} onChange={e => setInfo({ ...info, address: e.target.value })}
                     style={inputStyle} />
            </F>
            <F label={t('labFieldDesc')}>
              <textarea rows={4} value={info.description || ''} onChange={e => setInfo({ ...info, description: e.target.value })}
                        style={{ ...inputStyle, minHeight: 100, fontFamily: 'inherit' }} />
            </F>
            <div style={{ display: 'flex', gap: 8 }}>
              <F label={t('labFieldPhone')} style={{ flex: 1 }}>
                <input value={info.phone || ''} onChange={e => setInfo({ ...info, phone: e.target.value })}
                       placeholder="+998901234567" style={inputStyle} />
              </F>
              <F label={t('labFieldHours')} style={{ flex: 1 }}>
                <input value={info.working_hours || ''} onChange={e => setInfo({ ...info, working_hours: e.target.value })}
                       placeholder="10:00 - 04:00" style={inputStyle} />
              </F>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <F label="Instagram" style={{ flex: 1 }}>
                <input value={info.instagram || ''} onChange={e => setInfo({ ...info, instagram: e.target.value })}
                       placeholder="@cybernet_uz" style={inputStyle} />
              </F>
              <F label="Telegram" style={{ flex: 1 }}>
                <input value={info.telegram || ''} onChange={e => setInfo({ ...info, telegram: e.target.value })}
                       placeholder="@cybernet_bot" style={inputStyle} />
              </F>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <F label={t('labFieldParentPhone')} style={{ flex: 1 }}>
                <input value={info.parent_help_phone || ''} onChange={e => setInfo({ ...info, parent_help_phone: e.target.value })}
                       placeholder="+998901234567" style={inputStyle} />
              </F>
              <F label={t('labFieldEmergency')} style={{ flex: 1 }}>
                <input value={info.emergency_phone || ''} onChange={e => setInfo({ ...info, emergency_phone: e.target.value })}
                       placeholder="+998901234567" style={inputStyle} />
              </F>
            </div>

            <h3 style={{ marginTop: 20, marginBottom: 12, color: 'var(--accent)' }}>{t('labMapSection')}</h3>
            <p style={{ color: 'var(--text2)', fontSize: 12, marginBottom: 8 }}>{t('labMapHint')}</p>
            <div ref={mapRef} style={{ height: 300, border: '1px solid var(--border)' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <F label="Lat" style={{ flex: 1 }}>
                <input value={info.lat || ''} onChange={e => setInfo({ ...info, lat: e.target.value })}
                       style={inputStyle} />
              </F>
              <F label="Lng" style={{ flex: 1 }}>
                <input value={info.lng || ''} onChange={e => setInfo({ ...info, lng: e.target.value })}
                       style={inputStyle} />
              </F>
            </div>

            <button onClick={save} disabled={saving}
                    style={{ marginTop: 16, padding: '10px 20px', background: '#B6FF3C', color: '#000',
                             border: 0, fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
              {saving ? t('savingLabel') : `💾 ${t('save')}`}
            </button>
          </div>

          {/* O'NG: RASMLAR */}
          <div>
            {/* LOGO */}
            <h3 style={{ marginBottom: 12, color: 'var(--accent)' }}>🖼️ Klub Logosi</h3>
            <div style={{ padding: 16, border: '1px solid var(--border)', marginBottom: 20, background: 'var(--card2)' }}>
              <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
                Logo login ekranida va asosiy sahifa chap burchagida ko'rinadi. Tavsiya: kvadrat rasm (min 200×200 px).
              </p>
              <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                     style={{ display: 'none' }}
                     onChange={e => uploadLogo(e.target.files[0])} />
              {info.logo_url ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <img src={fullPhotoUrl(info.logo_url)} alt="logo"
                       style={{ width: 80, height: 80, objectFit: 'contain', border: '2px solid #B6FF3C', background: '#000', padding: 4 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                            style={{ padding: '8px 14px', background: 'var(--accent)', color: '#fff', border: 0, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {uploadingLogo ? 'Yuklanmoqda...' : '📷 Almashtirish'}
                    </button>
                    <button onClick={deleteLogo}
                            style={{ padding: '8px 14px', background: '#FF1E6E', color: '#fff', border: 0, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      🗑 O'chirish
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                        style={{ padding: '10px 20px', background: '#B6FF3C', color: '#000', border: 0, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  {uploadingLogo ? 'Yuklanmoqda...' : '⬆️ Logo yuklash'}
                </button>
              )}
            </div>

            <h3 style={{ marginBottom: 12, color: 'var(--accent)' }}>{t('labPhotosSection')}</h3>

            <div style={{ padding: 20, border: '2px dashed var(--border)', textAlign: 'center', marginBottom: 12 }}>
              <input ref={fileInputRef} type="file" multiple accept="image/*"
                     style={{ display: 'none' }}
                     onChange={e => uploadPhotos(e.target.files)} />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      style={{ padding: '10px 20px', background: 'var(--accent)', color: '#fff',
                               border: 0, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {uploading ? t('loading') : t('labUploadPhotos')}
              </button>
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text2)' }}>
                {t('labPhotoHint')}
              </div>
            </div>

            {info.cover_url && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{t('labCurrentCover')}</div>
                <img src={fullPhotoUrl(info.cover_url)} alt="cover"
                     style={{ width: '100%', maxHeight: 200, objectFit: 'cover', border: '2px solid #B6FF3C' }} />
              </div>
            )}

            <div style={{ marginBottom: 20, padding: 16, border: '1px solid var(--border)', background: 'var(--card2)' }}>
              <h3 style={{ marginTop: 0, marginBottom: 6, color: 'var(--accent)', fontSize: 16 }}>
                {t('labPaySection')}
              </h3>
              <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
                {t('labPayDesc')}
              </p>

              {/* CLICK */}
              <div style={{ marginBottom: 16, padding: 12, background: 'var(--card)', border: '1px solid #00c2ff40' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <b style={{ color: '#00c2ff' }}>🟢 CLICK</b>
                  <span style={{ fontSize: 11, color: info.click_merchant_id ? '#B6FF3C' : '#FF1E6E' }}>
                    {info.click_merchant_id ? t('labPayConfigured') : t('labPayNotConfigured')}
                  </span>
                </div>
                <F label="Merchant ID">
                  <input value={payCreds.click_merchant_id}
                         onChange={e => setPayCreds({ ...payCreds, click_merchant_id: e.target.value })}
                         placeholder="my.click.uz — Merchant"
                         style={inputStyle} />
                </F>
                <F label="Service ID">
                  <input value={payCreds.click_service_id}
                         onChange={e => setPayCreds({ ...payCreds, click_service_id: e.target.value })}
                         style={inputStyle} />
                </F>
                <F label={`Secret Key ${info.click_secret_set ? t('labSecretKeySet') : ''}`}>
                  <input type="password" value={payCreds.click_secret}
                         onChange={e => setPayCreds({ ...payCreds, click_secret: e.target.value })}
                         placeholder={info.click_secret_set ? t('labSecretKeepBlank') : t('labNewSecretKey')}
                         style={inputStyle} />
                </F>
                {info.click_merchant_id && (
                  <button onClick={() => removeProvider('click')}
                          style={{ padding: '6px 12px', background: 'transparent', color: '#FF1E6E',
                                   border: '1px solid #FF1E6E', fontSize: 12, cursor: 'pointer' }}>
                    {t('labRemoveClick')}
                  </button>
                )}
              </div>

              {/* PAYME */}
              <div style={{ marginBottom: 12, padding: 12, background: 'var(--card)', border: '1px solid #1aa3e840' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <b style={{ color: '#1aa3e8' }}>🔵 PAYME</b>
                  <span style={{ fontSize: 11, color: info.payme_merchant_id ? '#B6FF3C' : '#FF1E6E' }}>
                    {info.payme_merchant_id ? t('labPayConfigured') : t('labPayNotConfigured')}
                  </span>
                </div>
                <F label="Merchant ID">
                  <input value={payCreds.payme_merchant_id}
                         onChange={e => setPayCreds({ ...payCreds, payme_merchant_id: e.target.value })}
                         placeholder="merchant.paycom.uz"
                         style={inputStyle} />
                </F>
                <F label={`Secret Key ${info.payme_secret_set ? t('labSecretKeySet') : ''}`}>
                  <input type="password" value={payCreds.payme_secret}
                         onChange={e => setPayCreds({ ...payCreds, payme_secret: e.target.value })}
                         placeholder={info.payme_secret_set ? t('labSecretKeepBlank') : t('labNewSecretKey')}
                         style={inputStyle} />
                </F>
                {info.payme_merchant_id && (
                  <button onClick={() => removeProvider('payme')}
                          style={{ padding: '6px 12px', background: 'transparent', color: '#FF1E6E',
                                   border: '1px solid #FF1E6E', fontSize: 12, cursor: 'pointer' }}>
                    {t('labRemovePayme')}
                  </button>
                )}
              </div>

              <button onClick={savePaymentCreds} disabled={savingPay}
                      style={{ marginTop: 6, padding: '10px 20px', background: '#00c2ff', color: '#000',
                               border: 0, fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                {savingPay ? t('savingLabel') : t('labSavePayBtn')}
              </button>

              <div style={{ marginTop: 12, padding: 10, background: 'var(--card2)', fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>
                {t('labWebhookNote')}<br />
                Click Prepare: <code>{API_BASE}/api/payments/click/prepare</code><br />
                Click Complete: <code>{API_BASE}/api/payments/click/complete</code><br />
                Payme JSONRPC: <code>{API_BASE}/api/payments/payme</code>
              </div>
            </div>

            {/* HUB SECRET */}
            <div style={{ marginBottom: 20, padding: 16, border: '1px solid #B6FF3C40', background: 'var(--card2)' }}>
              <h3 style={{ marginTop: 0, marginBottom: 6, color: 'var(--accent)', fontSize: 16 }}>
                🔌 Club-Hub ulanish
              </h3>
              <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
                Club-Hub — klub ichidagi lokal server. Quyida secret yaratib, club-hub serveriga sozlang.
              </p>

              {hubSecret ? (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    Yangi Hub Secret (bir marta ko'rinadi)
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <input readOnly value={hubSecret}
                           style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, flex: 1,
                                    background: '#0a1a00', border: '1px solid #B6FF3C', color: '#B6FF3C' }} />
                    <button onClick={copyHubSecret}
                            style={{ padding: '8px 14px', background: hubCopied ? '#B6FF3C' : 'var(--card)',
                                     color: hubCopied ? '#000' : 'var(--text)', border: '1px solid #B6FF3C',
                                     cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                      {hubCopied ? '✓ Nusxalandi' : '📋 Nusxa'}
                    </button>
                  </div>
                  <button onClick={downloadHubConfig}
                          style={{ width: '100%', padding: '10px 16px', background: '#B6FF3C', color: '#000',
                                   border: 0, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginBottom: 8 }}>
                    ⬇ config.json yuklab olish
                  </button>
                  <div style={{ fontSize: 11, color: '#ff9800', lineHeight: 1.5 }}>
                    ⚠️ Secretni hoziroq nusxalang — sahifa yangilanganда ko'rinmaydi.<br />
                    config.json ni club-hub serveriga ko'chiring va qayta ishga tushiring.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.5 }}>
                    <b style={{ color: info?.hub_secret_set ? '#B6FF3C' : '#FF1E6E' }}>
                      {info?.hub_secret_set ? '🟢 Secret o\'rnatilgan' : '🔴 Secret yo\'q'}
                    </b>
                    {info?.hub_secret_set && (
                      <span style={{ marginLeft: 8 }}>— yangilash uchun qayta generate qiling</span>
                    )}
                  </div>
                  <button onClick={generateHubSecret} disabled={generatingHub}
                          style={{ width: '100%', padding: '10px 16px', background: 'var(--card)',
                                   color: 'var(--text)', border: '1px solid #B6FF3C',
                                   fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                    {generatingHub ? '⏳ Yaratilmoqda...' : '🔑 Hub Secret yaratish'}
                  </button>
                  {info?.hub_secret_set && (
                    <button onClick={downloadHubConfig}
                            style={{ width: '100%', marginTop: 8, padding: '10px 16px', background: 'transparent',
                                     color: '#B6FF3C', border: '1px solid #B6FF3C40',
                                     fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                      ⬇ config.json yuklab olish
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
              📸 {(info.photos || []).length} {t('labPhotoCount')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {(info.photos || []).map((url, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={fullPhotoUrl(url)} alt="" style={{ width: '100%', aspectRatio: 1, objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 4 }}>
                    <button onClick={() => setCover(url)}
                            style={{ background: '#B6FF3C', color: '#000', border: 0, padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}>
                      🏆
                    </button>
                    <button onClick={() => deletePhoto(url)}
                            style={{ background: '#FF1E6E', color: '#fff', border: 0, padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}>
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: 8,
  background: 'var(--card2)',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  fontSize: 14,
};
